import React from "react"
import { useTranslation } from "react-i18next"
import Typography, { TypographyProps } from "@/components/Typography"
import Block from "@/components/Block"
import Box from "@/components/Box"
import Button from "@/components/Button"
import Link from "@/components/Link"
import { ArrowRight24x14 } from "@/components/Icon"
import Flex from "@/components/Flex"
import { Container } from "./components/Container"
import SimpleFadeTextAnimation from "@/components/SimpleFadeTextAnimation"
import { useUserAgent } from "@/components/hooks/useUserAgent"
import { useSmartScript } from "@/utils/onelink/SmartScriptProvider"

const imageRemoteUrlObj = {
  OneTimeCard: `${process.env.ASSETS_BASE_URL}/pay/customer-page/onetimecard/One-time-Card.webp`,
  Cashback: `${process.env.ASSETS_BASE_URL}/pay/customer-page/onetimecard/cashback.webp`,
  Notification: `${process.env.ASSETS_BASE_URL}/pay/customer-page/onetimecard/notification.webp`,
}

const Text = ({ children, ...restProps }: TypographyProps) => {
  return (
    <Typography
      color={"#0B1426"}
      fontSize={[24, 24, 28]}
      fontWeight={[600]}
      lineHeight={["140%"]}
      {...restProps}
    >
      {children}
    </Typography>
  )
}

export const OneTimeCardSection = () => {
  const { t } = useTranslation("payCustomer")
  return (
    <Block bg="#FFF">
      <Container>
        <Typography
          color="#0B1426"
          textAlign={"center"}
          as="h2"
          variant="h2"
          mx={"auto"}
          mb={[4]}
        >
          {t("oneTimeCard.title")}
        </Typography>
        <Box
          display={["none", "flex", "flex"]}
          flexDirection={[null, "row", "column"]}
          customCss={{ gap: 2 }}
        >
          <Flex flexDirection={[null, "column", "row"]} customCss={{ gap: 2 }}>
            <SloganBlock1 />
            <PhoneBlock1 />
          </Flex>
          <Flex flexDirection={[null, "column", "row"]} customCss={{ gap: 2 }}>
            <PhoneBlock2 />
            <SloganBlock2 />
          </Flex>
        </Box>
        <Box
          display={["flex", "none", "none"]}
          flexDirection={"column"}
          customCss={{ gap: 2 }}
        >
          <PhoneBlock1 />
          <SloganBlock1 />
          <PhoneBlock2 />
          <SloganBlock2 />
        </Box>
      </Container>
    </Block>
  )
}

const SloganBlock1 = () => {
  const { t } = useTranslation("payCustomer")
  return (
    <Box
      width={["100%", 356, 441]}
      height={["auto", 134, 300]}
      bg={"#EAEEF4"}
      padding={[2, 2, "32px 28px"]}
      borderRadius={16}
    >
      <Text variant="p">{t("oneTimeCard.promotion_description")}</Text>
    </Box>
  )
}

const SloganBlock2 = () => {
  const { t } = useTranslation("payCustomer")
  const userAgent = useUserAgent()
  const { oneLinkUrl } = useSmartScript()
  const isIos = userAgent === "ios"
  const isAndroid = userAgent === "android"
  const neitherIosNorAndroid = !isIos && !isAndroid

  const buttonHref = neitherIosNorAndroid
    ? "https://crypto.com/product-news/shop-top-brands-with-crypto-pay"
    : oneLinkUrl
  return (
    <Flex
      flexDirection={"column"}
      justifyContent={"space-between"}
      alignItems={"flex-start"}
      width={["100%", 356, 441]}
      height={["auto", 160, 300]}
      bg={"#EAEEF4"}
      padding={[2, 2, "32px 28px"]}
      borderRadius={16}
    >
      <Text variant="p">{t("oneTimeCard.promotion_description_2")}</Text>
      <Link href={buttonHref}>
        <Button variant="secondaryLight" height={"44px !important"}>
          {t("oneTimeCard.button")}
          <ArrowRight24x14 ml={1} fontSize={24} paddingTop={"5px"} />
        </Button>
      </Link>
    </Flex>
  )
}

const PhoneBlock1 = () => (
  <Box
    width={["100%", 356, 623]}
    height={[250, 310, 300]}
    borderRadius={16}
    position={"relative"}
    customCss={{
      background: "linear-gradient(0deg, #D5D7DA 0%, #D5D7DA 100%)",
    }}
  >
    <Box
      as="img"
      position={"absolute"}
      width={[289, 308, 326]}
      alt={"one time card app open in phone with only bottom part"}
      src={imageRemoteUrlObj.OneTimeCard}
      loading={"lazy"}
      left={"50%"}
      top={0}
      customCss={{ transform: "translate(-50%, 0%)" }}
    />
  </Box>
)

const PhoneBlock2 = () => (
  <Box
    width={["100%", 356, 623]}
    height={[250, 284, 300]}
    borderRadius={16}
    customCss={{
      background: "linear-gradient(0deg, #D5D7DA 0%, #D5D7DA 100%)",
    }}
    position={"relative"}
  >
    <Box
      as="img"
      position={"absolute"}
      width={[289, 308, 326]}
      alt={"cashback app open in phone with only top part"}
      src={imageRemoteUrlObj.Cashback}
      loading={"lazy"}
      left={"50%"}
      bottom={0}
      customCss={{ transform: "translate(-50%, 0%)" }}
    />
    <Box
      position="absolute"
      top="50%"
      left={["7%", "7%", "24%"]}
      width={[283, 300, 320]}
    >
      <SimpleFadeTextAnimation delay={0.5}>
        <Box
          as="img"
          src={imageRemoteUrlObj.Notification}
          width={"100%"}
          alt="Pop-up notification"
          loading={"lazy"}
        />
      </SimpleFadeTextAnimation>
    </Box>
  </Box>
)
